// Phase 2 V7 (Apr 19 2026) — Custom voices list + CRUD hook.
//
// Unlike useCuratedVoices (hot-cached module-level because the catalog
// is static-per-deploy), custom voices are user-scoped and mutate on
// every create/rename/delete — so no module cache here. Instead: the
// hook owns local state and exposes mutation actions that refetch.
import { useCallback, useEffect, useState } from "react";
import {
  createCustomVoice,
  deleteCustomVoice,
  listCustomVoices,
  renameCustomVoice,
} from "@/services/ads/videos/customVoiceService";
import type {
  CreateCustomVoiceRequest,
  CustomVoiceResponse,
} from "@/services/ads/videos/videoDraft.types";

export interface UseCustomVoicesResult {
  voices: CustomVoiceResponse[];
  isLoading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
  create: (payload: CreateCustomVoiceRequest) => Promise<CustomVoiceResponse | null>;
  rename: (id: string | number, displayName: string) => Promise<CustomVoiceResponse | null>;
  remove: (id: string | number) => Promise<boolean>;
}

export function useCustomVoices(enabled: boolean = true): UseCustomVoicesResult {
  const [voices, setVoices] = useState<CustomVoiceResponse[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    const { res } = await listCustomVoices();
    if (res) {
      setVoices(res);
    } else {
      setError(true);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
  }, [enabled, refresh]);

  const create = useCallback(
    async (payload: CreateCustomVoiceRequest) => {
      const { res } = await createCustomVoice(payload);
      if (res) {
        setVoices((prev) => [res, ...prev]);
        return res;
      }
      return null;
    },
    []
  );

  const rename = useCallback(
    async (id: string | number, displayName: string) => {
      const { res } = await renameCustomVoice(id, { display_name: displayName });
      if (res) {
        setVoices((prev) =>
          prev.map((v) => (v.voice_id === res.voice_id ? res : v))
        );
        return res;
      }
      return null;
    },
    []
  );

  const remove = useCallback(async (id: string | number) => {
    const { error: err } = await deleteCustomVoice(id);
    if (!err) {
      setVoices((prev) => prev.filter((v) => !v.voice_id.endsWith(`-${id}`)));
      return true;
    }
    return false;
  }, []);

  return { voices, isLoading, error, refresh, create, rename, remove };
}
